import React, { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Edit2, Trash2, GraduationCap, X, Check } from 'lucide-react';

const DEGREES = ['MBBS', 'MD', 'MS', 'DM', 'MCh', 'BDS', 'MDS', 'BAMS', 'BHMS', 'BUMS', 'BPT', 'MPT', 'BSc Nursing', 'GNM', 'ANM', 'DNB', 'Diploma', 'PhD', 'Other'];
const YEARS = Array.from({ length: 60 }, (_, i) => new Date().getFullYear() - i);

export default function Step3Education({ data = [], onSave }) {
  const [entries, setEntries] = useState(Array.isArray(data) ? data : []);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(entries.length === 0);
  const emptyEntry = { college: '', university: '', degree: '', field: '', start_year: '', end_year: '', country: 'India', state: '', city: '' };
  const [form, setForm] = useState(emptyEntry);

  useEffect(() => { setEntries(Array.isArray(data) ? data : []); }, [data]);
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleAdd = () => {
    if (!form.college || !form.degree || !form.end_year) return;
    const newEntries = editing !== null ? entries.map((e, i) => i === editing ? form : e) : [...entries, form];
    setEntries(newEntries);
    setForm(emptyEntry);
    setEditing(null);
    setShowForm(false);
  };
  const handleEdit = (i) => { setForm(entries[i]); setEditing(i); setShowForm(true); };
  const handleDelete = (i) => setEntries(entries.filter((_, idx) => idx !== i));
  const handleCancel = () => { setForm(emptyEntry); setEditing(null); setShowForm(false); };

  return (
    <form id="step-form-3" onSubmit={(e) => { e.preventDefault(); onSave(entries); }} className="p-6 lg:p-8 space-y-5">
      {entries.length > 0 && (
        <div className="space-y-3">
          {entries.map((edu, i) => (
            <div key={i} className="p-4 rounded-xl border border-slate-200 bg-slate-50 flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#3B1C73]/10 flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-5 h-5 text-[#3B1C73]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900">{edu.degree}{edu.field ? ` in ${edu.field}` : ''}</p>
                <p className="text-sm text-slate-600">{edu.college}{edu.university ? `, ${edu.university}` : ''}</p>
                <p className="text-xs text-slate-500 mt-1">{edu.city}{edu.state ? `, ${edu.state}` : ''} · {edu.start_year || '?'} - {edu.end_year}</p>
              </div>
              <div className="flex gap-1">
                <button type="button" onClick={() => handleEdit(i)} className="p-2 text-slate-500 hover:text-[#3B1C73] hover:bg-white rounded-lg" data-testid={`edu-edit-${i}`}><Edit2 className="w-4 h-4" /></button>
                <button type="button" onClick={() => handleDelete(i)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" data-testid={`edu-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm ? (
        <div className="p-5 rounded-xl border-2 border-[#3B1C73]/20 bg-purple-50/30 space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="font-semibold text-slate-900">{editing !== null ? 'Edit Degree' : 'Add Degree'}</h4>
            <button type="button" onClick={handleCancel} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Degree *</Label>
              <Select value={form.degree} onValueChange={(v) => update('degree', v)}>
                <SelectTrigger className="mt-1.5" data-testid="edu-degree"><SelectValue placeholder="Select degree" /></SelectTrigger>
                <SelectContent className="max-h-72">{DEGREES.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Field / Specialization</Label><Input value={form.field} onChange={(e) => update('field', e.target.value)} placeholder="e.g. Cardiology" className="mt-1.5" data-testid="edu-field" /></div>
            <div><Label>College / Institution *</Label><Input required value={form.college} onChange={(e) => update('college', e.target.value)} placeholder="e.g. AIIMS Delhi" className="mt-1.5" data-testid="edu-college" /></div>
            <div><Label>University</Label><Input value={form.university} onChange={(e) => update('university', e.target.value)} placeholder="e.g. Delhi University" className="mt-1.5" /></div>
            <div>
              <Label>Start Year</Label>
              <Select value={form.start_year?.toString()} onValueChange={(v) => update('start_year', parseInt(v))}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Year" /></SelectTrigger>
                <SelectContent className="max-h-72">{YEARS.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>End Year *</Label>
              <Select value={form.end_year?.toString()} onValueChange={(v) => update('end_year', parseInt(v))}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Year" /></SelectTrigger>
                <SelectContent className="max-h-72">{YEARS.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>City</Label><Input value={form.city} onChange={(e) => update('city', e.target.value)} placeholder="e.g. Delhi" className="mt-1.5" /></div>
            <div><Label>State</Label><Input value={form.state} onChange={(e) => update('state', e.target.value)} placeholder="e.g. Delhi" className="mt-1.5" /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={handleCancel} className="px-4 py-2 text-slate-600 hover:bg-white rounded-lg">Cancel</button>
            <button type="button" onClick={handleAdd} className="px-4 py-2 bg-[#3B1C73] text-white rounded-lg hover:bg-[#2c1457] flex items-center gap-1 text-sm font-medium" data-testid="edu-save"><Check className="w-4 h-4" /> {editing !== null ? 'Update' : 'Add'}</button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setShowForm(true)} className="w-full py-4 border-2 border-dashed border-slate-300 hover:border-[#3B1C73] hover:bg-[#3B1C73]/5 rounded-xl text-slate-600 hover:text-[#3B1C73] font-medium flex items-center justify-center gap-2" data-testid="edu-add-btn">
          <Plus className="w-5 h-5" /> Add {entries.length > 0 ? 'Another' : 'Your'} Degree
        </button>
      )}

      {entries.length === 0 && !showForm && (
        <p className="text-center text-slate-400 text-sm">No education added yet — this step is optional but recommended</p>
      )}
    </form>
  );
}
